import { useState, useEffect } from "react"
import { getIn, useFormikContext } from "formik"

import * as Yup from 'yup'
import {
  BillMode,
  FormikBillingDetails,
  FormikInspectorAgreeCheck,
  FormikNotesArea,
  billingDetailsSchema,
} from "../../../../../../components/widgets"

import { cloneDeep } from "lodash"
import { DLMROI, extractLabelSelectionDetails, DLMLabelSelectionDetails } from "../../../../../core/doclabel"
import { useDLMResponseFormContext } from "../../components"

export const initDLMSubmitInspectionValues = {
  verdict: '',
  worflow: '',
  appType: '',
  note: '',
  noteAttachments: [],
  labelsNotUsed:[],
  inspectorAgree: false,
}

export const DLMSubmitInspectionSchema = Yup.object({
  verdict: Yup.string().trim(),
  workflow: Yup.string().trim(),
  note: Yup.string().trim().required().label('Note'),
  noteAttachments: Yup.array(),
  labelsNotUsed: Yup.array(),
  inspectorAgree: Yup.bool().oneOf([true], 'Must be checked').label('Confirmation')
}).concat(billingDetailsSchema)

const DLMSubmitInspectionForm = () =>  {
  const { setLabelSeries, setLabelSelection, setTargetLabels, setOnLabelSelect } = useDLMResponseFormContext();
  const { values, setFieldValue } = useFormikContext();

  const [ labelDetails, setLabelDetails] = useState<Array<DLMLabelSelectionDetails>>([]);

  const verdict = getIn(values, 'verdict') ?? '';
  const workflow = getIn(values, 'workflow') ?? '';
  const labelsNotUsed = getIn(values, 'labelsNotUsed') ?? [];
  const siteROIModels = getIn(values, 'siteROIModels') ?? null;
  
  const initLabelSelection = (e: any) => {
    const elem = e.target.closest('.labelRow');

    if (! (elem && elem.dataset.idx)) return;
    const idx = parseInt(elem.dataset.idx);
    const obj = labelDetails[idx];

    if (! (obj && idx > -1)) return;

    setLabelSeries(idx);
    setLabelSelection(obj.labelCodes);
    setTargetLabels(obj.targetLabels ? obj.targetLabels : [])
    setOnLabelSelect(() => handleLabelSelect);
  }

  const handleLabelSelect = (selected: Array<string>, index: number) => {

    if (! (index > -1)) return;

    let _labelDetails = cloneDeep(labelDetails);

    let _selected = cloneDeep(labelDetails[index]);


    Object.assign(_selected, { targetLabels: (selected && selected.length > 0) ? selected : null });

    _labelDetails.splice(index, 1, _selected);

    let _labelsNotUsed: Array<string> = [];
    _labelDetails.forEach( _details => {
      if(! (_details?.targetLabels && _details.targetLabels.length > 0)) return;
      _labelsNotUsed = _labelsNotUsed.concat(_details.targetLabels);
    })

    _labelsNotUsed.sort();

    setFieldValue('labelsNotUsed', _labelsNotUsed, true);

    setLabelDetails(_labelDetails);

    setLabelSeries(-1);
    setLabelSelection(null);
    setOnLabelSelect(undefined);
  }

  useEffect(() => {
    if (! (siteROIModels && siteROIModels.length > 0)) return;

    let _labelDetails: Array<DLMLabelSelectionDetails> = [];
    siteROIModels.forEach((_site: any) => {
      if (! (_site.hasOwnProperty('roiModels') && _site.roiModels.length > 0)) return;
      _site.roiModels.forEach( (roi: DLMROI) => {
        if (! (roi?.roiDetails && roi.roiDetails.length > 0)) return;
        const _details = extractLabelSelectionDetails(roi.roiDetails);
        if (! (_details && _details.length > 0)) return;

        _labelDetails = _labelDetails.concat(_details);
      })
    })
    setLabelDetails(_labelDetails);
  }, [siteROIModels, workflow])

  useEffect(() => {
    const VERDICT_BILLING_MAP: Record<string, BillMode> = {
      approved: 'YES',
      rejected: 'OPTIONAL',
    };

    // Falls back to 'NO' if verdict is not in the map
    const billingRequired = VERDICT_BILLING_MAP[verdict] ?? 'NO';
    setFieldValue('billingRequired', billingRequired, true);
  }, [verdict, setFieldValue]);

  return(
  <>
  { verdict === 'approved' &&
  <>
  <div className='fw-bold fs-6'>
    Labels Not Used:
    { (labelsNotUsed && labelsNotUsed.length > 0) && <span className="ms-2">{`(${labelsNotUsed.length})`}</span>}
  </div>
  <div className="">
  { (labelsNotUsed && labelsNotUsed.length > 0) ? (
    <span className="fs-7">{labelsNotUsed.join(' | ')}</span>
  ) : (
    <span className="text-muted fst-italic">(no selection)</span>
  )
  }
  </div>

  <div className='text-muted fs-7 mt-4'>
    Select from Label Series
  </div>
  <div className='separator border-gray-200 my-0'></div>
  <div className='table-responsive min-h-80px mh-200px pb-4'>
    <table
      id='kt_table_roi_labels'
      className='table align-middle table-row-bordered table-row-gray-300 fs-7 gy-2 dataTable no-footer'
    >
      <thead>
        <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
          <th className="p-2 w-125px">Series</th>
          <th className="p-2 w-100px">Action</th>
          <th className="p-2">NOT USED</th>
        </tr>
      </thead>
      { (labelDetails && labelDetails.length > 0) &&
      <tbody className='text-gray-800 align-middle fs-6'>
        {
        labelDetails.map((obj , idx) => (
        <tr key={`labelDetail_${idx}`} className="labelRow" data-idx={idx}>
          <td className="p-2 align-top fs-7">
            <>
            {obj.labelFrom}
            -
            {obj.labelTo}
            </>
          </td>
          <td className="p-2 align-top">
            <button
              type="button"
              className="siteBtn btn btn-light btn-active-primary px-3 py-1 fs-7"
              onClick={initLabelSelection}
              title="Select Label Split"
            >
              Select
            </button>
            {
            (obj?.targetLabels && obj.targetLabels.length > 0) &&
            <div className="d-inline-block text-muted fs-7 mt-1 ms-1">
              ({obj.targetLabels.length})
            </div>
            }
          </td>
          <td className="p-2 align-top fs-7">
            {
            (obj?.targetLabels && obj.targetLabels.length > 0) ? (
            <span className="fs-7">{obj.targetLabels.join(' | ')}</span>
            ) : (
            <span className="text-muted fst-italic">(no selection)</span>
            )
            }
          </td>
        </tr>
        ))
        }
      </tbody>
      }
    </table>
  </div>
  <div className='separator border-gray-200 my-0'></div>
  </>
  }

  <FormikBillingDetails />
  <FormikNotesArea required />
  <FormikInspectorAgreeCheck />
  </>
  )

}

export { DLMSubmitInspectionForm }